import { Link } from "react-router-dom";
import { Check, X, Printer, Mail, Phone } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { FadeUp } from "../components/Reveal";
import { useContent } from "../context/ContentContext";

const SectionHead = ({ num, kicker, title }) => (
  <div className="mb-6">
    <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-[#8E8D8A]">{num}. {kicker}</p>
    {title && <h2 className="mt-1 font-display text-2xl font-bold uppercase tracking-wide text-[#111111] sm:text-3xl">{title}</h2>}
  </div>
);

export default function Garantie() {
  const { site, garantie } = useContent();
  const {
    versie: garantieVersie, termijnen: garantieTermijnen, dekking: garantieDekking,
    dekkingToelichting: garantieDekkingToelichting, uitsluitingen: garantieUitsluitingen,
    voorwaarden: garantieVoorwaarden, procedure: garantieProcedure,
  } = garantie;
  const bedrijfsregel = [site.address, site.kvk && `KvK ${site.kvk}`, site.btw && `Btw ${site.btw}`].filter(Boolean);

  return (
    <main data-testid="garantie-page">
      <PageHero
        eyebrow="Garantievoorwaarden"
        title="Tot 10 jaar garantie op mijn werk."
        intro="De garantietermijn verschilt per onderdeel en type werkzaamheden. Hieronder lees je precies wat onder mijn garantie valt, hoe lang, onder welke voorwaarden en hoe je er een beroep op doet."
      />

      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-4 pb-16 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3 border-y border-[#E2E0D9] py-3 text-xs text-[#8E8D8A]">
            <span data-testid="garantie-versie">{garantieVersie} · {site.name}, {site.location}</span>
            <button type="button" data-testid="garantie-print" onClick={() => window.print()} className="flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-wide text-[#111111] hover:underline">
              <Printer className="h-3.5 w-3.5" /> Printen of opslaan als PDF
            </button>
          </div>

          <div className="mt-12">
            <SectionHead num="1" kicker="Duur van de garantie" title="Garantietermijnen per onderdeel" />
            <div className="grid gap-4 lg:grid-cols-2">
              {garantieTermijnen.map((t, i) => (
                <FadeUp key={t.titel} delay={i * 0.05}>
                  <div data-testid={`garantie-termijn-${i}`} className="flex h-full gap-5 border border-[#DDD9D0] bg-white p-6">
                    <div className="w-28 shrink-0 border-r border-[#E2E0D9] pr-4">
                      <p className={`font-display font-bold uppercase leading-none tracking-wide text-[#111111] ${t.duur.length > 8 ? "text-sm" : "text-2xl"}`}>{t.duur}</p>
                    </div>
                    <div>
                      <h3 className="font-display text-base font-bold uppercase tracking-wide text-[#111111]">{t.titel}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-[#555555]">{t.tekst}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
            <p className="mt-4 text-sm text-[#8E8D8A]">Alle termijnen gelden vanaf de datum van oplevering, zoals vermeld op de factuur of het opleverformulier.</p>
          </div>

          <div className="mt-16 grid gap-10 lg:grid-cols-2">
            <FadeUp>
              <SectionHead num="2" kicker="Dekking" title="Wat valt onder de garantie?" />
              <ul className="space-y-3">
                {garantieDekking.map((d) => (
                  <li key={d} className="flex gap-3 text-[15px] leading-relaxed text-[#444444]">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center bg-cream"><Check className="h-3.5 w-3.5 text-[#111111]" /></span>
                    {d}
                  </li>
                ))}
              </ul>
              <p className="mt-5 border-l-2 border-cream pl-4 text-sm leading-relaxed text-[#555555]">{garantieDekkingToelichting}</p>
            </FadeUp>
            <FadeUp delay={0.05}>
              <SectionHead num="3" kicker="Uitsluitingen" title="Wat valt er niet onder?" />
              <ul className="space-y-3">
                {garantieUitsluitingen.map((d) => (
                  <li key={d} className="flex gap-3 text-[15px] leading-relaxed text-[#444444]">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center bg-[#E2E0D9]"><X className="h-3.5 w-3.5 text-[#555555]" /></span>
                    {d}
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="bg-[#EFEDE8]">
        <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6">
          <SectionHead num="4" kicker="Voorwaarden" title="Wanneer geldt de garantie?" />
          <ol className="max-w-4xl space-y-4">
            {garantieVoorwaarden.map((v, i) => (
              <FadeUp key={i} delay={i * 0.04}>
                <li data-testid={`garantie-voorwaarde-${i}`} className="flex gap-4 border-b border-[#DDD9D0] pb-4 text-[15px] leading-relaxed text-[#444444]">
                  <span className="font-display text-xl font-bold text-[#B9B7B0]">{i + 1}</span>
                  <span>{v}</span>
                </li>
              </FadeUp>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6">
          <SectionHead num="5" kicker="Procedure" title="Zo doe je een beroep op de garantie" />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {garantieProcedure.map((s, i) => (
              <FadeUp key={s.titel} delay={i * 0.06}>
                <div data-testid={`garantie-stap-${i}`} className="h-full border border-[#DDD9D0] bg-white p-6">
                  <span className="font-display text-2xl font-bold text-[#B9B7B0]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-lg font-bold uppercase tracking-wide text-[#111111]">{s.titel}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#555555]">{s.tekst}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={`mailto:${site.email}?subject=Garantiemelding`} data-testid="garantie-mail" className="inline-flex items-center gap-2 rounded-[4px] bg-cream px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-[#111111] transition-colors duration-200 hover:bg-cream-deep">
              <Mail className="h-4 w-4" /> Garantiemelding mailen
            </a>
            <a href={site.phoneHref} data-testid="garantie-bel" className="inline-flex items-center gap-2 rounded-[4px] border border-[#111111] px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-[#111111] transition-colors duration-200 hover:border-cream hover:bg-cream">
              <Phone className="h-4 w-4" /> {site.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1400px] px-4 pb-16 sm:px-6">
          <FadeUp>
            <div className="max-w-4xl border border-[#DDD9D0] bg-[#EFEDE8] p-6 sm:p-8">
              <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-[#8E8D8A]">6. Je wettelijke rechten</p>
              <h2 className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-[#111111] sm:text-2xl">Deze garantie komt bovenop de wet, niet in plaats daarvan.</h2>
              <p className="mt-3 text-[15px] leading-relaxed text-[#555555]">
                Deze commerciële garantie wordt verstrekt door {site.name}, {site.location}. Zij laat de wettelijke rechten die je als consument hebt op grond van het Burgerlijk Wetboek (waaronder het recht op deugdelijk uitgevoerd werk en het recht op kosteloos herstel of vervanging bij non-conformiteit) volledig onverlet. Je kunt altijd een beroep doen op die wettelijke rechten, ook als de garantietermijn hierboven is verstreken.
              </p>
              <div className="mt-6 border-t border-[#DDD9D0] pt-4 text-sm text-[#555555]">
                <p className="font-display font-bold uppercase tracking-wide text-[#111111]">Garantieverstrekker: {site.name}</p>
                <p className="mt-1">{site.location} · Tel. {site.phoneDisplay} · <a href={`mailto:${site.email}`} className="underline underline-offset-2">{site.email}</a></p>
                {bedrijfsregel.length > 0 && <p className="mt-1">{bedrijfsregel.join(" · ")}</p>}
                <p className="mt-2 text-xs text-[#8E8D8A]">
                  Op al mijn offertes en overeenkomsten zijn deze garantievoorwaarden en mijn{" "}
                  <Link to="/algemene-voorwaarden" className="underline underline-offset-2">algemene voorwaarden</Link> van toepassing. Bij tegenstrijdigheid gaat de offerte voor. {garantieVersie}.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
